import { useContext, useState } from "react";
import { FormLayout, TextField, Button, Card, Text, BlockStack, Icon, InlineStack, Tooltip } from "@shopify/polaris";
import { Field } from "@shopify/react-form";
import { DeleteIcon, PlusIcon, PlusCircleIcon, InfoIcon } from "@shopify/polaris-icons";
import { FixedBoxConfig } from "../Types";
import { AppConfigContext } from "../../../context/AppConfigContext";
import { getNumberFieldSuffix } from "../../../utilities/constants";

interface FixedBoxesConfigProps {
    fixed_boxes: Field<FixedBoxConfig[]>;
    dispatchBundleConfig: any;
    fixed_boxes_upsell_message: Field<string>;
    fixed_boxes_success_message: Field<string>;
}

const FixedBoxesConfig = ({
    fixed_boxes,
    dispatchBundleConfig,
    fixed_boxes_upsell_message,
    fixed_boxes_success_message,
}: FixedBoxesConfigProps) => {
    const { money_format } = useContext(AppConfigContext);
    const [activeBoxIndex, setActiveBoxIndex] = useState(0);

    const boxes = fixed_boxes.value || [];
    const activeBox = boxes[activeBoxIndex];

    const handleAddBox = () => {
        let newItemCount = boxes.length > 0 ? boxes[boxes.length - 1].itemsCount + 1 : 1;
        // Ensure the new item count is unique
        while (boxes.some((box) => box.itemsCount === newItemCount)) {
            newItemCount++;
        }
        const newBox: FixedBoxConfig = {
            itemsCount: newItemCount,
            fixedPrice: boxes.length > 0 ? boxes[boxes.length - 1].fixedPrice + 10 : 50,
            boxName: `Box of ${newItemCount}`,
        };
        const newBoxes = [...boxes, newBox];
        fixed_boxes.onChange(newBoxes);
        dispatchBundleConfig({ key: "fixed_boxes", payload: newBoxes });
        setActiveBoxIndex(newBoxes.length - 1);
    };

    const handleRemoveBox = (index: number) => {
        if (boxes.length <= 1) return;
        const newBoxes = boxes.filter((_, i) => i !== index);
        fixed_boxes.onChange(newBoxes);
        dispatchBundleConfig({ key: "fixed_boxes", payload: newBoxes });
        if (activeBoxIndex >= newBoxes.length) {
            setActiveBoxIndex(Math.max(0, newBoxes.length - 1));
        }
    };

    const handleUpdateBox = (index: number, key: keyof FixedBoxConfig, value: any) => {
        const newBoxes = boxes.map((box, i) => (i === index ? { ...box, [key]: value } : box));
        fixed_boxes.onChange(newBoxes);
        dispatchBundleConfig({ key: "fixed_boxes", payload: newBoxes });
    };

    return (
        <BlockStack gap="400">
            <BlockStack gap="200">
                <InlineStack gap="100" align="start">
                    <Text as="h3" variant="headingSm" fontWeight="bold">
                        2. Configure Your Boxes
                    </Text>
                    <Tooltip
                        content="Boxes are sorted by item quantity and unlock as customers add more products."
                        width="wide"
                    >
                        <Icon source={InfoIcon} tone="subdued" />
                    </Tooltip>
                </InlineStack>
                <Text as="p" variant="bodyMd">
                    Define the different box sizes and their fixed prices.
                </Text>
            </BlockStack>

            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "24px",
                    overflowX: "auto",
                    // paddingBottom: "8px",
                    borderBottom: "1px solid #E1E3E5",
                    width: 0,
                    minWidth: "100%",
                    flexWrap: "nowrap",
                }}
            >
                {boxes.map((_, index) => (
                    <button
                        key={index}
                        onClick={() => setActiveBoxIndex(index)}
                        style={{
                            padding: "8px 16px",
                            backgroundColor: activeBoxIndex === index ? "#f4f6f8" : "transparent",
                            color: activeBoxIndex === index ? "#202223" : "#6D7175",
                            cursor: "pointer",
                            whiteSpace: "nowrap",
                            fontWeight: activeBoxIndex === index ? "bold" : "normal",
                            fontSize: "14px",
                            border: "1px solid transparent",
                            borderBottom: activeBoxIndex === index ? "none" : "1px solid transparent",
                            borderRadius: "8px 8px 0 0",
                            flexShrink: 0,
                            ...(activeBoxIndex === index && {
                                border: "1px solid #E1E3E5",
                                borderBottom: "1px solid white",
                                marginBottom: "-1px",
                            }),
                        }}
                    >
                        Box {index + 1}
                    </button>
                ))}
                {boxes.length < 10 && (
                    <button
                        onClick={handleAddBox}
                        style={{
                            padding: "8px 16px",
                            backgroundColor: "transparent",
                            border: "none",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            color: "#005BD3",
                            fontSize: "14px",
                            fontWeight: "500",
                            flexShrink: 0,
                        }}
                    >
                        <Icon source={PlusIcon} tone="interactive" />
                        Add Box
                    </button>
                )}
            </div>

            {activeBox && (
                <div style={{ backgroundColor: "#f4f6f8", borderRadius: "8px" }}>
                    <Card padding="400" background="bg-surface-secondary">
                        <BlockStack gap="400">
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                }}
                            >
                                <Text as="span" variant="bodyMd" fontWeight="bold">
                                    Configuring: Box {activeBoxIndex + 1}
                                </Text>
                                <Button
                                    icon={DeleteIcon}
                                    tone="critical"
                                    variant="tertiary"
                                    onClick={() => handleRemoveBox(activeBoxIndex)}
                                    disabled={boxes.length <= 1}
                                >
                                    Delete
                                </Button>
                            </div>
                            <FormLayout>
                                <FormLayout.Group>
                                    <BlockStack gap="100">
                                        <TextField
                                            label="Items in Box"
                                            type="number"
                                            value={activeBox.itemsCount.toString()}
                                            onChange={(val) =>
                                                handleUpdateBox(activeBoxIndex, "itemsCount", parseInt(val) || 0)
                                            }
                                            autoComplete="off"
                                            min={1}
                                            suffix="Items"
                                            error={
                                                activeBox.itemsCount <= 0
                                                    ? "Items count must be greater than zero"
                                                    : boxes.some(
                                                            (box, i) =>
                                                                i !== activeBoxIndex &&
                                                                box.itemsCount === activeBox.itemsCount,
                                                        )
                                                      ? "Quantity already exists in another box"
                                                      : false
                                            }
                                        />
                                    </BlockStack>
                                    <TextField
                                        label="Fixed Price"
                                        type="number"
                                        value={activeBox.fixedPrice.toString()}
                                        onChange={(val) =>
                                            handleUpdateBox(activeBoxIndex, "fixedPrice", parseFloat(val) || 0)
                                        }
                                        suffix={getNumberFieldSuffix("fixed", money_format).suffix}
                                        autoComplete="off"
                                        min={0}
                                        error={
                                            activeBox.fixedPrice <= 0 ? "Fixed price must be greater than zero" : false
                                        }
                                    />
                                </FormLayout.Group>

                                <TextField
                                    label="Box Name"
                                    type="text"
                                    value={activeBox.boxName || ""}
                                    onChange={(val) => handleUpdateBox(activeBoxIndex, "boxName", val)}
                                    autoComplete="off"
                                    error={!activeBox.boxName?.trim() ? "Box name cannot be empty" : false}
                                    maxLength={30}
                                    showCharacterCount
                                />
                            </FormLayout>
                        </BlockStack>
                    </Card>
                </div>
            )}

            {!activeBox && boxes.length === 0 && (
                <div
                    style={{
                        textAlign: "center",
                        padding: "40px",
                        border: "1px dashed #E1E3E5",
                        borderRadius: "8px",
                    }}
                >
                    <BlockStack gap="200" align="center">
                        <Text as="p" tone="subdued">
                            No boxes configured yet.
                        </Text>
                        <Button onClick={handleAddBox} icon={PlusCircleIcon}>
                            Add your first box
                        </Button>
                    </BlockStack>
                </div>
            )}
            <BlockStack gap="200">
                <Text as="h3" variant="headingSm" fontWeight="bold">
                    3. Configure Your Messages
                </Text>
                <Card background="bg-surface-secondary">
                    <BlockStack gap="400">
                        <TextField
                            label="Upsell Message"
                            placeholder="text"
                            autoComplete="off"
                            maxLength={80}
                            {...fixed_boxes_upsell_message}
                            onBlur={fixed_boxes_upsell_message?.onBlur}
                            showCharacterCount
                            onChange={(val) => {
                                dispatchBundleConfig({
                                    payload: val,
                                    key: "fixed_boxes_upsell_message",
                                });
                                fixed_boxes_upsell_message.onChange(val);
                            }}
                        />
                        <TextField
                            label="Success Message"
                            placeholder="text"
                            autoComplete="off"
                            maxLength={80}
                            {...fixed_boxes_success_message}
                            onBlur={fixed_boxes_success_message?.onBlur}
                            showCharacterCount
                            onChange={(val) => {
                                dispatchBundleConfig({
                                    payload: val,
                                    key: "fixed_boxes_success_message",
                                });
                                fixed_boxes_success_message.onChange(val);
                            }}
                        />
                    </BlockStack>
                </Card>
            </BlockStack>
        </BlockStack>
    );
};

export default FixedBoxesConfig;
